/*
Edge clones for an endless carousel: copies of the last `perSide` slides go
before the first and copies of the first `perSide` after the last, so a step
past either end lands on a look-alike; once the scroll settles the carousel
teleports to the matching real slide. Clones are marked with `attribute`,
hidden from assistive tech, inert and lazy so they never compete with the
real slides. Lazy is not enough in a horizontal ring: the browser fetches lazy
images within a wide margin, so with `holdImages` the clones that start off
screen get no image addresses until the carousel releases them (after page
load); the clone next to the first slide keeps its image — its edge is visible
at once. onScrollSettle tells a carousel when the scroll has come to rest,
which is the moment to check for a clone (twins: loopMath.js).
*/

// Long enough for snap to finish, short enough that the teleport feels instant.
export const SETTLE_MS = 120;

/**
 * @param {Element} track Scroll container holding the slides.
 * @param {Element[]} slides Real slides, in order.
 * @param {{perSide: number, attribute: string, holdImages?: boolean}} clones How many copies
 *   go on each edge, which attribute marks them, whether off-screen clones wait for images.
 * @returns {() => void} Gives the held clones their images back (does nothing without holdImages).
 */
export default function addEdgeClones(track, slides, { perSide, attribute, holdImages = false }) {
    const copy = (slide) => markClone(slide.cloneNode(true), attribute);
    const before = slides.slice(-perSide).map(copy);
    const after = slides.slice(0, perSide).map(copy);
    // Addresses go before the clones enter the page, or the browser starts the fetch.
    const offScreen = [...before.slice(0, -1), ...after];
    const releases = holdImages ? offScreen.flatMap(holdImageAddresses) : [];

    track.prepend(...before);
    track.append(...after);
    return () => releases.forEach((release) => release());
}

/**
 * Calls `callback` once the track has not scrolled for SETTLE_MS.
 * @param {EventTarget} track Scrolling element.
 * @param {() => void} callback Reads the resting position itself, at call time.
 * @param {AbortSignal} signal Ends the watch: removes the listener and drops a pending call.
 */
export function onScrollSettle(track, callback, signal) {
    let timer = null;

    track.addEventListener('scroll', () => {
        clearTimeout(timer);
        timer = setTimeout(callback, SETTLE_MS);
    }, { passive: true, signal });
    signal.addEventListener('abort', () => clearTimeout(timer), { once: true });
}

// Takes src/srcset off the clone's images and <source>s; each returned function puts one back.
function holdImageAddresses(clone) {
    return [...clone.querySelectorAll('img, source')].map((element) => {
        const saved = ['src', 'srcset']
            .filter((name) => element.hasAttribute(name))
            .map((name) => [name, element.getAttribute(name)]);

        saved.forEach(([name]) => element.removeAttribute(name));
        return () => saved.forEach(([name, value]) => element.setAttribute(name, value));
    });
}

function markClone(clone, attribute) {
    clone.setAttribute(attribute, '');
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    clone.querySelectorAll('img').forEach((img) => {
        img.loading = 'lazy';
        img.removeAttribute('fetchpriority');
    });
    return clone;
}
